import React, { useCallback, useRef } from 'react';
import { HStack, VStack } from '@chakra-ui/react';
import { Box } from '@chakra-ui/layout';
import { Button } from '@chakra-ui/button';
import LightGallery from 'lightgallery/react';

import 'lightgallery/css/lightgallery.css';
import 'lightgallery/css/lg-zoom.css';
import 'lightgallery/css/lg-thumbnail.css';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import { LightGalleryContainer } from './ImageGalleryStyles';
import { ObjectImage } from '../../../types/ObjectTypes';

export interface ImageGalleryProps {
  images: ObjectImage[];
  showMore: boolean;
}

export const ImageGallery = (props: ImageGalleryProps): JSX.Element => {
  const { images, showMore } = props;
  const firstImage = useRef(null);

  const handleClick = useCallback(() => {
    // @ts-ignore
    firstImage.current.click();
  }, [firstImage]);

  return (
    <>
      {images.length === 0 && (
        <Box
          w={'100%'}
          h={'100%'}
          backgroundSize={'cover'}
          backgroundImage={`url("https://via.placeholder.com/150")`}
          backgroundPosition={'center'}
        />
      )}
      {images.length > 0 && (
        <LightGalleryContainer>
          <LightGallery
            exThumbImage={'data-exthumbimage'}
            selector={'.content-overlay'}
            mobileSettings={{ controls: true, showCloseIcon: true, download: true }}
            plugins={[lgThumbnail]}
            licenseKey={process.env.REACT_APP_LIGHT_GALLERY_KEY}>
            <HStack h={'100%'} gridGap={1} overflow={'hidden'}>
              <Box
                ref={firstImage}
                className={'content-overlay'}
                borderTopLeftRadius={{ base: 0, md: 8 }}
                borderBottomLeftRadius={{ base: 0, md: 8 }}
                flex={1}
                h={'100%'}
                data-src={images[0].url}
                data-exthumbimage={images[0].url}
                backgroundColor={'black'}
                backgroundSize={'cover'}
                backgroundPosition={'center center'}
                backgroundImage={`url("${images[0].url}")`}
                filter={'brightness(100%)'}
                transition={'filter 0.25s ease-in-out'}
                _hover={{ cursor: 'pointer', filter: 'brightness(50%)' }}
                pt={{ base: '75%', md: 0 }}
              />
              {images.length > 1 && (
                <VStack gridGap={1} display={{ base: 'none', md: 'flex' }} w={'37%'} h={'100%'}>
                  <Box
                    className={'content-overlay'}
                    flex={1}
                    width={'100%'}
                    backgroundSize={'cover'}
                    backgroundPosition={'center center'}
                    data-src={images[1].url}
                    data-exthumbimage={images[1].url}
                    backgroundImage={`url("${images[1].url}")`}
                    filter={'brightness(100%)'}
                    transition={'filter 0.25s ease-in-out'}
                    _hover={{ cursor: 'pointer', filter: 'brightness(50%)' }}
                  />
                  {images.length > 2 && (
                    <>

                      <Box
                        className={'content-overlay'}
                        flex={1}
                        width={'100%'}
                        backgroundSize={'cover'}
                        backgroundPosition={'center center'}
                        data-src={images[2].url}
                        data-exthumbimage={images[2].url}
                        backgroundImage={`url("${images[2].url}")`}
                        filter={'brightness(100%)'}
                        transition={'filter 0.25s ease-in-out'}
                        _hover={{ cursor: 'pointer', filter: 'brightness(50%)' }}
                      />

                      {(showMore && images.length > 3)  && (
                        <Box h={0} position={'relative'} bg={'red.100'} w={'100%'}>
                          <Box
                            opacity={0.4}
                            bg={'black'}
                            h={12}
                            w={'100%'}
                            bottom={0}
                            position={'absolute'}
                          />
                          <Button
                            onClick={handleClick}
                            size={'sm'}
                            _hover={{ backgroundColor: 'gray.800' }}
                            color={'gray.50'}
                            variant={'outline'}
                            position={'absolute'}
                            right={4}
                            bottom={2}>
                            Alle Bilder {images.length} anzeigen
                          </Button>
                        </Box>
                      )}
                    </>

                  )}
                </VStack>
              )}
            </HStack>
            {images.slice(3).map((image, idx) => {
              return (
                <Box
                  key={idx}
                  display={'none'}
                  className={'content-overlay'}
                  data-src={image.url}
                  data-exthumbimage={image.url}
                />
              );
            })}
          </LightGallery>
        </LightGalleryContainer>
      )}
    </>
  );
};
